import React from 'react';
import {
   Box,
   Button,
   Checkbox,
   Dialog,
   Flex,
   Heading,
   Select,
   Separator,
   Text,
   TextField,
} from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import APNavBar from 'Components/adminpanel/Elements/APNavBar';
import APSidebar from 'Components/adminpanel/APSidebar/APSidebar';
import { FormField } from 'shared/components';
import { ErrorOutlineIcon, IconWest } from 'shared/icons';
import { PlanEditStore } from './plan-edit.store';
import { usePlanEdit } from './plan-edit.provider';

import './feature-plan-edit.scss';

type LimitFeatureProps = {
   store: PlanEditStore;
   feature: 'job_post' | 'resume_download' | 'contact_view' | 'profile_view' | 'job_apply';
   label: string;
   error?: string;
};

const optionEntries = (options: Record<string, string>) => Object.entries(options);

const LimitFeature = observer(({ store, feature, label, error }: LimitFeatureProps) => {
   return (
      <Box className="feature-plan-edit__limit">
         <FormField label={label} error={error}>
            <TextField.Root
               size="3"
               color="indigo"
               type="number"
               min="1"
               value={store.isUnlimited(feature) ? '' : String(store[feature])}
               disabled={store.isUnlimited(feature)}
               onChange={(e) => store.setLimitValue(feature, e.target.value)}
            />
         </FormField>

         <Box className="feature-plan-edit__checkbox-label">
            <Checkbox
               size="3"
               color="indigo"
               checked={store.isUnlimited(feature)}
               onCheckedChange={(checked) => store.toggleUnlimited(feature, checked === true)}
            />
            <Text size="2">Unlimited</Text>
         </Box>
      </Box>
   );
});

const PlanEditView = () => {
   const planEdit = usePlanEdit();
   const navigate = useNavigate();
   const [confirmOpen, setConfirmOpen] = React.useState(false);

   React.useEffect(() => {
      planEdit.loadPlan();
      window.scrollTo(0, 0);
   }, []);

   const requestSubmit = () => {
      if (planEdit.validate()) {
         setConfirmOpen(true);
      }
   };

   return (
      <>
         <APNavBar />
         <Box pt="80px" className="feature-plan-edit">
            <Flex width="270px" minWidth="270px">
               <APSidebar />
            </Flex>

            <Box className="feature-plan-edit__content">
               <Flex className="feature-plan-edit__header" justify="between" align="center">
                  <Box>
                     <Flex gap="2" align="center" mb="2">
                        <Button
                           variant="ghost"
                           size="1"
                           onClick={() => navigate('/admin/admins/dashboard')}
                        >
                           Dashboard
                        </Button>
                        <Text color="gray">/</Text>
                        <Button
                           variant="ghost"
                           size="1"
                           onClick={() => navigate('/admin/manage-plans')}
                        >
                           Plan List
                        </Button>
                        <Text color="gray">/</Text>
                        <Text size="2" color="gray">
                           Edit Plan
                        </Text>
                     </Flex>

                     <Heading as="h1" weight="medium">
                        Edit Plan
                     </Heading>
                  </Box>
               </Flex>

               {planEdit.isLoading ? (
                  <Flex className="feature-plan-edit__loading" align="center" justify="center">
                     <Text color="gray">Loading plan...</Text>
                  </Flex>
               ) : (
                  <Box className="feature-plan-edit__form">
                     <Flex direction="column" gap="4">
                        <FormField label="Plan Name" required error={planEdit.errors.plan_name}>
                           <TextField.Root
                              size="3"
                              color="indigo"
                              placeholder="Plan Name"
                              value={planEdit.formGroup.controls.plan_name.value}
                              onChange={(e) => planEdit.setBaseField('plan_name', e.target.value)}
                           />
                        </FormField>

                        <FormField label="Full Plan Name" error={planEdit.errors.full_plan_name}>
                           <TextField.Root
                              size="3"
                              color="indigo"
                              placeholder="Full Plan Name"
                              value={planEdit.formGroup.controls.full_plan_name.value}
                              onChange={(e) =>
                                 planEdit.setBaseField('full_plan_name', e.target.value)
                              }
                           />
                        </FormField>

                        <Box className="feature-plan-edit__fields-row">
                           <Box className="feature-plan-edit__field-cell">
                              <FormField label="Type" required error={planEdit.errors.type}>
                                 <Select.Root
                                    size="3"
                                    value={planEdit.formGroup.controls.type.value}
                                    onValueChange={(value) => planEdit.setBaseField('type', value)}
                                 >
                                    <Select.Trigger color="indigo" placeholder="Select Plan Type" />
                                    <Select.Content position="popper">
                                       {optionEntries(planEdit.planTypeOptions).map(
                                          ([key, value]) => (
                                             <Select.Item key={key} value={key}>
                                                {value}
                                             </Select.Item>
                                          )
                                       )}
                                    </Select.Content>
                                 </Select.Root>
                              </FormField>
                           </Box>

                           <Box className="feature-plan-edit__field-cell">
                              <FormField
                                 label="User Plan"
                                 required
                                 error={planEdit.errors.planuser}
                              >
                                 <Select.Root
                                    size="3"
                                    value={planEdit.formGroup.controls.planuser.value}
                                    onValueChange={(value) =>
                                       planEdit.setBaseField('planuser', value)
                                    }
                                 >
                                    <Select.Trigger color="indigo" placeholder="Select User Plan" />
                                    <Select.Content position="popper">
                                       {optionEntries(planEdit.userTypeOptions).map(
                                          ([key, value]) => (
                                             <Select.Item key={key} value={key}>
                                                {value}
                                             </Select.Item>
                                          )
                                       )}
                                    </Select.Content>
                                 </Select.Root>
                              </FormField>
                           </Box>
                        </Box>

                        <Box className="feature-plan-edit__fields-row">
                           <Box className="feature-plan-edit__field-cell">
                              <FormField
                                 label={planEdit.timePeriodLabel}
                                 required
                                 error={planEdit.errors.type_value}
                              >
                                 <TextField.Root
                                    size="3"
                                    color="indigo"
                                    placeholder="Time Period"
                                    value={planEdit.formGroup.controls.type_value.value}
                                    onChange={(e) =>
                                       planEdit.setBaseField('type_value', e.target.value)
                                    }
                                 />
                              </FormField>
                           </Box>

                           <Box className="feature-plan-edit__field-cell">
                              <FormField
                                 label="Plan Amount"
                                 required
                                 error={planEdit.errors.amount}
                              >
                                 <TextField.Root
                                    size="3"
                                    color="indigo"
                                    placeholder="Plan Amount"
                                    value={planEdit.formGroup.controls.amount.value}
                                    onChange={(e) =>
                                       planEdit.setBaseField('amount', e.target.value)
                                    }
                                 />
                              </FormField>
                           </Box>
                        </Box>
                     </Flex>

                     {planEdit.isEmployerPlan && (
                        <>
                           <Separator size="4" my="5" />
                           <Flex direction="column" gap="4">
                              <Box>
                                 <Text size="2" color="gray">
                                    Select Features
                                    <Text as="span" color="red">
                                       *
                                    </Text>
                                 </Text>
                                 {planEdit.errors.emp_checkbx && (
                                    <Text as="div" size="2" color="red" mt="1">
                                       {planEdit.errors.emp_checkbx}
                                    </Text>
                                 )}
                              </Box>

                              <Box className="feature-plan-edit__feature-grid">
                                 <Box className="feature-plan-edit__checkbox-label">
                                    <Checkbox
                                       size="3"
                                       color="indigo"
                                       checked={planEdit.hasJobPost}
                                       onCheckedChange={(checked) =>
                                          planEdit.toggleFeature('job_post', checked === true)
                                       }
                                    />
                                    <Text size="2">Number of Job Post</Text>
                                 </Box>

                                 <Box className="feature-plan-edit__checkbox-label">
                                    <Checkbox
                                       size="3"
                                       color="indigo"
                                       checked={planEdit.hasResumeDownload}
                                       onCheckedChange={(checked) =>
                                          planEdit.toggleFeature(
                                             'resume_download',
                                             checked === true
                                          )
                                       }
                                    />
                                    <Text size="2">Number of resume download</Text>
                                 </Box>

                                 <Box className="feature-plan-edit__checkbox-label">
                                    <Checkbox
                                       size="3"
                                       color="indigo"
                                       checked={planEdit.hasContactView}
                                       onCheckedChange={(checked) =>
                                          planEdit.toggleFeature('contact_view', checked === true)
                                       }
                                    />
                                    <Text size="2">Number of Contact Views</Text>
                                 </Box>

                                 <Box className="feature-plan-edit__checkbox-label">
                                    <Checkbox
                                       size="3"
                                       color="indigo"
                                       checked={planEdit.hasCandidateSearch}
                                       onCheckedChange={(checked) =>
                                          planEdit.toggleFeature(
                                             'candidate_search',
                                             checked === true
                                          )
                                       }
                                    />
                                    <Text size="2">Access candidate search functionality</Text>
                                 </Box>

                                 <Box className="feature-plan-edit__checkbox-label">
                                    <Checkbox
                                       size="3"
                                       color="indigo"
                                       checked={planEdit.hasProfileView}
                                       onCheckedChange={(checked) =>
                                          planEdit.toggleFeature('profile_view', checked === true)
                                       }
                                    />
                                    <Text size="2">Number of Candidate Profile Views</Text>
                                 </Box>
                              </Box>

                              {planEdit.hasJobPost && (
                                 <LimitFeature
                                    store={planEdit}
                                    feature="job_post"
                                    label="Number of Job Post"
                                    error={planEdit.errors.job_post}
                                 />
                              )}

                              {planEdit.hasResumeDownload && (
                                 <LimitFeature
                                    store={planEdit}
                                    feature="resume_download"
                                    label="Number of Resume Downloads"
                                    error={planEdit.errors.resume_download}
                                 />
                              )}

                              {planEdit.hasContactView && (
                                 <LimitFeature
                                    store={planEdit}
                                    feature="contact_view"
                                    label="Number of Contact Views"
                                    error={planEdit.errors.contact_view}
                                 />
                              )}

                              {planEdit.hasProfileView && (
                                 <LimitFeature
                                    store={planEdit}
                                    feature="profile_view"
                                    label="Number of Candidate Profile Views"
                                    error={planEdit.errors.profile_view}
                                 />
                              )}
                           </Flex>
                        </>
                     )}

                     {planEdit.isJobseekerPlan && (
                        <>
                           <Separator size="4" my="5" />
                           <Flex direction="column" gap="4">
                              <Box>
                                 <Text size="2" color="gray">
                                    Select Features
                                    <Text as="span" color="red">
                                       *
                                    </Text>
                                 </Text>
                                 {planEdit.errors.job_apply_checkbx && (
                                    <Text as="div" size="2" color="red" mt="1">
                                       {planEdit.errors.job_apply_checkbx}
                                    </Text>
                                 )}
                              </Box>

                              <Box className="feature-plan-edit__checkbox-label">
                                 <Checkbox
                                    size="3"
                                    color="indigo"
                                    checked={planEdit.hasJobApply}
                                    onCheckedChange={(checked) =>
                                       planEdit.toggleFeature('job_apply', checked === true)
                                    }
                                 />
                                 <Text size="2">Number of Job Apply</Text>
                              </Box>

                              {planEdit.hasJobApply && (
                                 <LimitFeature
                                    store={planEdit}
                                    feature="job_apply"
                                    label="Number of Job Apply"
                                    error={planEdit.errors.job_apply_input}
                                 />
                              )}
                           </Flex>
                        </>
                     )}

                     <Flex className="feature-plan-edit__actions" gap="3" justify="end">
                        <Button
                           variant="outline"
                           size="3"
                           color="indigo"
                           highContrast
                           onClick={() => navigate('/admin/manage-plans')}
                        >
                           <IconWest width={18} height={18} />
                           Back
                        </Button>
                        <Button size="3" onClick={requestSubmit}>
                           SAVE
                        </Button>
                     </Flex>
                  </Box>
               )}
            </Box>
         </Box>

         <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(e) => e.preventDefault()}
            >
               <Box className="deactivated-dialog">
                  <Flex justify="center">
                     <Text color="red">
                        <ErrorOutlineIcon width={80} height={80} />
                     </Text>
                  </Flex>

                  <Flex justify="center" direction="column" align="center" mt="5" mb="6">
                     <Dialog.Title aria-describedby={undefined}>
                        <Text>Update Plan?</Text>
                     </Dialog.Title>
                     <Text>Do you want to update this Plan?</Text>
                  </Flex>

                  <Flex justify="center" gap="3">

                     <Dialog.Close>
                        <Button color="gray" size="3">
                           Cancel
                        </Button>
                     </Dialog.Close>

                     <Button
                        color="red"
                        size="3"
                        loading={planEdit.isSaving}
                        onClick={() =>
                           planEdit.submit({
                              onSuccess: () => {
                                 setConfirmOpen(false);
                                 toast.success('Plan updated successfully!');
                              },
                              onError: (message) => {
                                 toast.error(
                                    message || 'Could not update plan. Please try again later!'
                                 );
                              },
                           })
                        }
                     >
                        Confirm
                     </Button>
                  </Flex>
               </Box>
            </Dialog.Content>
         </Dialog.Root>
      </>
   );
};

export const FeaturePlanEditView = observer(PlanEditView);
